export interface DocumentSnippet {
    trigger: string;
    name: string;
    body: string;
}

// NOTE: placeholder syntax is ${N:default} — parsed by expandSnippetBody in PaperEditor
// We wrap in a function call to avoid TypeScript treating ${} as template holes
function s(body: string): string { return body; }

export const DOCUMENT_SNIPPETS: DocumentSnippet[] = [
    // ── Structure ──────────────────────────────────────────────────────────
    { trigger: "en", name: "Generic env", body: s("\\begin{${1:env}}\n  ${0}\n\\end{${1:env}}") },
    { trigger: "sec", name: "Section", body: s("\\section{${1:Title}}\n${0}") },
    { trigger: "ssec", name: "Subsection", body: s("\\subsection{${1:Title}}\n${0}") },
    { trigger: "sssec", name: "Subsubsection", body: s("\\subsubsection{${1:Title}}\n${0}") },
    { trigger: "thm", name: "Theorem", body: s("\\begin{theorem}\n  ${0}\n\\end{theorem}") },
    { trigger: "lem", name: "Lemma", body: s("\\begin{lemma}\n  ${0}\n\\end{lemma}") },
    { trigger: "prop", name: "Proposition", body: s("\\begin{proposition}\n  ${0}\n\\end{proposition}") },
    { trigger: "cor", name: "Corollary", body: s("\\begin{corollary}\n  ${0}\n\\end{corollary}") },
    { trigger: "defn", name: "Definition", body: s("\\begin{definition}\n  ${0}\n\\end{definition}") },
    { trigger: "pf", name: "Proof", body: s("\\begin{proof}\n  ${0}\n\\end{proof}") },
    { trigger: "eq", name: "Equation", body: s("\\begin{equation}\n  ${0}\n\\end{equation}") },
    { trigger: "ali", name: "Align", body: s("\\begin{align}\n  ${0}\n\\end{align}") },
    { trigger: "alin", name: "Align*", body: s("\\begin{align*}\n  ${0}\n\\end{align*}") },
    { trigger: "enum", name: "Enumerate", body: s("\\begin{enumerate}\n  \\item ${0}\n\\end{enumerate}") },
    { trigger: "enI", name: "Enumerate (Roman)", body: s("\\begin{enumerate}[label=\\textnormal{(\\Roman*)}]\n  \\item ${0}\n\\end{enumerate}") },
    { trigger: "eni", name: "Enumerate (roman)", body: s("\\begin{enumerate}[label=\\textnormal{(\\roman*)}]\n  \\item ${0}\n\\end{enumerate}") },
    { trigger: "enA", name: "Enumerate (Alpha)", body: s("\\begin{enumerate}[label=\\textnormal{(\\Alph*)}]\n  \\item ${0}\n\\end{enumerate}") },
    { trigger: "ena", name: "Enumerate (alpha)", body: s("\\begin{enumerate}[label=\\textnormal{(\\alph*)}]\n  \\item ${0}\n\\end{enumerate}") },
    { trigger: "item", name: "Itemize", body: s("\\begin{itemize}\n  \\item ${0}\n\\end{itemize}") },
    { trigger: "fig", name: "Figure", body: s("\\begin{figure}[htbp]\n  \\centering\n  \\includegraphics[width=0.8\\textwidth]{${1:file}}\n  \\caption{${2:Caption}}\n  \\label{fig:${3:label}}\n\\end{figure}\n${0}") },
    { trigger: "tikz", name: "TikZ picture", body: s("\\begin{tikzpicture}\n  ${0}\n\\end{tikzpicture}") },
    { trigger: "tbl", name: "Table", body: s("\\begin{table}[htbp]\n  \\centering\n  \\caption{${1:Caption}}\n  \\label{tab:${2:label}}\n  \\begin{tabular}{${3:cc}}\n    ${0}\n  \\end{tabular}\n\\end{table}") },
    { trigger: "vb", name: "Verbatim (code)", body: s("\\begin{verbatim}\n${0}\n\\end{verbatim}") },
    { trigger: "code", name: "Code block", body: s("\\begin{verbatim}\n${0}\n\\end{verbatim}") },

    // ── Math delimiters ────────────────────────────────────────────────────
    { trigger: "mm", name: "Inline math", body: s("$${1}$${0}") },
    { trigger: "mk", name: "Inline math (alt)", body: s("$${1:math}$${0}") },
    { trigger: "dm", name: "Display math", body: s("\\[\n  ${0}\n\\]") },

    // ── Fractions ─────────────────────────────────────────────────────────
    { trigger: "ff", name: "Fraction", body: s("\\frac{${1:num}}{${2:den}}${0}") },
    { trigger: "df", name: "Dfrac", body: s("\\dfrac{${1:num}}{${2:den}}${0}") },
    { trigger: "pder", name: "Partial deriv", body: s("\\frac{\\partial ${1:f}}{\\partial ${2:x}}${0}") },
    { trigger: "dv", name: "d/dx derivative", body: s("\\frac{d ${1:f}}{d ${2:x}}${0}") },
    { trigger: "div", name: "Division (slash)", body: s("${1:a}/${2:b}${0}") },

    // ── Sums / products / integrals ────────────────────────────────────────
    { trigger: "sum", name: "Sum", body: s("\\sum_{${1:i=1}}^{${2:n}} ${0}") },
    { trigger: "prod", name: "Product", body: s("\\prod_{${1:i=1}}^{${2:n}} ${0}") },
    { trigger: "lim", name: "Limit", body: s("\\lim_{${1:n} \\to ${2:\\infty}} ${0}") },
    { trigger: "int", name: "Integral", body: s("\\int_{${1:a}}^{${2:b}} ${0} \\, d${3:x}") },
    { trigger: "iint", name: "Double integral", body: s("\\iint_{${1:D}} ${0} \\, d${2:A}") },
    { trigger: "oint", name: "Contour integral", body: s("\\oint_{${1:C}} ${0} \\, d${2:z}") },

    // ── Square root / nth root ─────────────────────────────────────────────
    { trigger: "sq", name: "Square root", body: s("\\sqrt{${1:x}}${0}") },
    { trigger: "nrt", name: "Nth root", body: s("\\sqrt[${1:n}]{${2:x}}${0}") },

    // ── Superscript / subscript ────────────────────────────────────────────
    { trigger: "td", name: "Superscript", body: s("^{${1:n}}${0}") },
    { trigger: "rd", name: "Subscript", body: s("_{${1:i}}${0}") },
    { trigger: "cb", name: "Cube", body: s("^{3}${0}") },
    { trigger: "sr", name: "Squared", body: s("^{2}${0}") },

    // ── Delimiters ─────────────────────────────────────────────────────────
    { trigger: "lr(", name: "lr( )", body: s("\\left( ${0} \\right)") },
    { trigger: "lr[", name: "lr[ ]", body: s("\\left[ ${0} \\right]") },
    { trigger: "lr{", name: "lr{ }", body: s("\\left\\{ ${0} \\right\\}") },
    { trigger: "lr|", name: "lr| |", body: s("\\left| ${0} \\right|") },
    { trigger: "lr<", name: "lr< >", body: s("\\left\\langle ${0} \\right\\rangle") },
    { trigger: "norm", name: "Norm", body: s("\\left\\| ${1:v} \\right\\|") },

    // ── Math fonts ────────────────────────────────────────────────────────
    { trigger: "mcal", name: "mathcal", body: s("\\mathcal{${1:A}}${0}") },
    { trigger: "mbb", name: "mathbb", body: s("\\mathbb{${1:R}}${0}") },
    { trigger: "mbf", name: "mathbf", body: s("\\mathbf{${1:v}}${0}") },
    { trigger: "mrm", name: "mathrm", body: s("\\mathrm{${1:A}}${0}") },

    // ── Accents ────────────────────────────────────────────────────────────
    { trigger: "hat", name: "Hat", body: s("\\hat{${1:a}}${0}") },
    { trigger: "bar", name: "Bar", body: s("\\bar{${1:a}}${0}") },
    { trigger: "vec", name: "Vec", body: s("\\vec{${1:a}}${0}") },
    { trigger: "dot", name: "Dot", body: s("\\dot{${1:a}}${0}") },
    { trigger: "ddot", name: "Ddot", body: s("\\ddot{${1:a}}${0}") },
    { trigger: "tld", name: "Tilde", body: s("\\tilde{${1:a}}${0}") },

    // ── Common symbols ─────────────────────────────────────────────────────
    { trigger: "inf", name: "Infinity", body: s("\\infty") },
    { trigger: "iff", name: "Iff", body: s("\\iff") },
    { trigger: "=>>", name: "Implies", body: s("\\implies") },
    { trigger: "=<<", name: "Impliedby", body: s("\\impliedby") },
    { trigger: "nabl", name: "Nabla", body: s("\\nabla") },
    { trigger: "xx", name: "Times", body: s("\\times") },
    { trigger: "ox", name: "Otimes", body: s("\\otimes") },
    { trigger: "opl", name: "Oplus", body: s("\\oplus") },
    { trigger: "cdot", name: "Cdot", body: s("\\cdot") },
    { trigger: "**", name: "Cdot (star)", body: s("\\cdot") },
    { trigger: "pm", name: "Plusminus", body: s("\\pm") },

    // ── Greek letters ─────────────────────────────────────────────────────
    { trigger: ";a", name: "alpha", body: s("\\alpha") },
    { trigger: ";b", name: "beta", body: s("\\beta") },
    { trigger: ";g", name: "gamma", body: s("\\gamma") },
    { trigger: ";G", name: "Gamma", body: s("\\Gamma") },
    { trigger: ";d", name: "delta", body: s("\\delta") },
    { trigger: ";D", name: "Delta", body: s("\\Delta") },
    { trigger: ";e", name: "epsilon", body: s("\\epsilon") },
    { trigger: ";ve", name: "varepsilon", body: s("\\varepsilon") },
    { trigger: ";z", name: "zeta", body: s("\\zeta") },
    { trigger: ";h", name: "eta", body: s("\\eta") },
    { trigger: ";q", name: "theta", body: s("\\theta") },
    { trigger: ";Q", name: "Theta", body: s("\\Theta") },
    { trigger: ";vq", name: "vartheta", body: s("\\vartheta") },
    { trigger: ";k", name: "kappa", body: s("\\kappa") },
    { trigger: ";l", name: "lambda", body: s("\\lambda") },
    { trigger: ";L", name: "Lambda", body: s("\\Lambda") },
    { trigger: ";m", name: "mu", body: s("\\mu") },
    { trigger: ";n", name: "nu", body: s("\\nu") },
    { trigger: ";x", name: "xi", body: s("\\xi") },
    { trigger: ";X", name: "Xi", body: s("\\Xi") },
    { trigger: ";p", name: "pi", body: s("\\pi") },
    { trigger: ";P", name: "Pi", body: s("\\Pi") },
    { trigger: ";r", name: "rho", body: s("\\rho") },
    { trigger: ";s", name: "sigma", body: s("\\sigma") },
    { trigger: ";S", name: "Sigma", body: s("\\Sigma") },
    { trigger: ";vs", name: "varsigma", body: s("\\varsigma") },
    { trigger: ";t", name: "tau", body: s("\\tau") },
    { trigger: ";u", name: "upsilon", body: s("\\upsilon") },
    { trigger: ";U", name: "Upsilon", body: s("\\Upsilon") },
    { trigger: ";f", name: "phi", body: s("\\phi") },
    { trigger: ";vf", name: "varphi", body: s("\\varphi") },
    { trigger: ";F", name: "Phi", body: s("\\Phi") },
    { trigger: ";c", name: "chi", body: s("\\chi") },
    { trigger: ";y", name: "psi", body: s("\\psi") },
    { trigger: ";Y", name: "Psi", body: s("\\Psi") },
    { trigger: ";w", name: "omega", body: s("\\omega") },
    { trigger: ";W", name: "Omega", body: s("\\Omega") },
];

export const SNIPPET_LOOKUP = new Map(
    DOCUMENT_SNIPPETS.map((snippet) => [snippet.trigger, snippet])
);
